import { useEffect, useState, useCallback } from "react";
import {
  useQuery,
  getEdgyReadiness,
  getEdgyCreditState,
  getEdgeReportById,
  askEdgyQuestion,
} from "wasp/client/operations";
import { Sparkles, AlertCircle, Loader2, BookOpen, Plus, Lock } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { useLanguageContext } from "../client/contexts/LanguageContext";
import { Button } from "../client/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import PlaybookForm from "../playbook/PlaybookForm";
import AddTradeModal from "../trading-journal/AddTradeModal";
import EdgyScanSequence from "./EdgyScanSequence";
import EdgeReportView from "./EdgeReportView";
import { useEdgyAnalysis } from "./useEdgyAnalysis";
import { useEdgyAccess } from "./useEdgyAccess";
import { edgeReportSchema, type EdgeReportContent } from "./reportSchema";
import { edgyCreditsLabel } from "./creditsLabel";

/**
 * Edgy's analysis modal.
 *
 * A wide centred dialog rather than a side drawer: the report is dense (score,
 * breakdowns, actions) and reads badly in a 520px column. Built on the app's shared
 * Dialog, so overlay, focus trap, Escape and portalling are the standard ones.
 *
 * UX rule from the spec: Edgy never asks the trader a question. The only interaction
 * in the whole flow is picking a strategy — and that step is skipped when there is
 * only one. Everything else advances on its own.
 *
 * z-index note: the Botpress launcher sits at 9999 and is a separate support widget.
 * The dialog stays below it, so the launcher is hidden while this is open (CSS in
 * Main.css, keyed on the `edgy-drawer-open` body class).
 */

type QaEntry = { question: string; answer: string; askedAt: string };

export default function EdgyDrawer({
  open,
  onClose,
  onUpgrade,
}: {
  open: boolean;
  onClose: () => void;
  /** Mode Lite : ouvre la vraie modale de plans au lieu de naviguer vers /pricing. */
  onUpgrade?: () => void;
}) {
  const t = useTranslation();
  const { language } = useLanguageContext();

  const analysis = useEdgyAnalysis();
  const { state, setState, start, reset, cancel } = analysis;

  const [selectedStrategy, setSelectedStrategy] = useState<{ id: string; name: string } | null>(null);
  const [showPlaybookForm, setShowPlaybookForm] = useState(false);
  const [showAddTrade, setShowAddTrade] = useState(false);
  const [askingQuestion, setAskingQuestion] = useState<string | null>(null);
  const [questionError, setQuestionError] = useState(false);

  const { data: readiness, refetch: refetchReadiness } = useQuery(getEdgyReadiness, {}, { enabled: open });
  const { data: credits, refetch: refetchCredits } = useQuery(getEdgyCreditState, {}, { enabled: open });
  const { data: report, refetch: refetchReport } = useQuery(
    getEdgeReportById,
    { id: analysis.reportId ?? "" },
    { enabled: !!analysis.reportId }
  );

  // Beta entitlement. `credits` carries the server's verdict, so once it has
  // loaded it overrides the plan-based guess and the gate follows the server flag.
  const { isLocked } = useEdgyAccess(credits);

  const language2: "fr" | "en" = language === "fr" ? "fr" : "en";

  /** Launches the analysis for a strategy — no confirmation step by design. */
  const launch = useCallback(
    (playbookId: string, name: string) => {
      setSelectedStrategy({ id: playbookId, name });
      start({ playbookId, language: language2 });
    },
    [start, language2]
  );

  // CHECKING: decide where the drawer lands as soon as readiness is known.
  useEffect(() => {
    if (!open || !readiness || !credits) return;
    if (state !== "IDLE" && state !== "CHECKING") return;

    if (readiness.killSwitch) {
      setState("ERROR");
      return;
    }
    // Entitlement first: a locked plan must see the upgrade screen, not a
    // "create a playbook" checklist for a feature it cannot run anyway.
    if (isLocked) {
      setState("BETA_LOCKED");
      return;
    }
    if (!readiness.hasPlaybook || !readiness.hasTrades || readiness.strategies.length === 0) {
      setState("MISSING_PREREQS");
      return;
    }
    if (!credits.hasCredit) {
      setState("NO_CREDITS");
      return;
    }
    if (readiness.strategies.length === 1) {
      // Single strategy: skip the picker entirely and go straight to scanning.
      const only = readiness.strategies[0];
      launch(only.playbookId, only.name);
      return;
    }
    setState("SELECT_STRATEGY");
  }, [open, readiness, credits, state, setState, launch, isLocked]);

  // Entering the drawer starts the check.
  useEffect(() => {
    if (open && state === "IDLE") setState("CHECKING");
  }, [open, state, setState]);

  // Leaving resets everything so a reopen starts clean.
  useEffect(() => {
    if (!open) {
      cancel();
      reset();
      setSelectedStrategy(null);
      setShowPlaybookForm(false);
      setQuestionError(false);
    }
  }, [open, cancel, reset]);

  // Escape and background scroll locking now come from Radix Dialog. Closing on
  // Escape just aborts a running analysis (the spec forbids a confirmation step;
  // the credit is only spent on success anyway).

  // The Botpress support launcher lives at z-index 9999 and would float over the
  // modal, bottom-right. Hide it while the modal is open (CSS in Main.css).
  useEffect(() => {
    if (!open) return;
    document.body.classList.add("edgy-drawer-open");
    return () => document.body.classList.remove("edgy-drawer-open");
  }, [open]);

  const parsedReport: EdgeReportContent | null = (() => {
    if (!report?.report) return null;
    const result = edgeReportSchema.safeParse(report.report);
    return result.success ? result.data : null;
  })();

  const qaThread = (report?.qaThread as QaEntry[] | null) ?? [];
  const answeredQuestions = new Set(qaThread.map((q) => q.question));

  async function handleAskQuestion(question: string) {
    if (!analysis.reportId || askingQuestion) return;
    setAskingQuestion(question);
    setQuestionError(false);
    try {
      await askEdgyQuestion({ reportId: analysis.reportId, question });
      await refetchReport();
      await refetchCredits();
    } catch {
      setQuestionError(true);
    } finally {
      setAskingQuestion(null);
    }
  }

  async function handlePrereqDone() {
    setShowPlaybookForm(false);
    await refetchReadiness();
    // Back to CHECKING: the effect above re-routes, and the analysis chains on its own.
    setState("CHECKING");
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
        {/* max-w-4xl: the report is dense and needs the width, while staying under
            the comparator's 5xl so the app keeps one modal scale. The content box
            itself does not scroll — the body below does, so the header and the
            credits footer stay pinned while a long report scrolls. */}
        <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-4xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="flex-row items-center gap-2 space-y-0 border-b border-border px-4 py-3 pr-12 text-left">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500">
              <Sparkles className="h-4 w-4 text-white" />
            </span>
            <div>
              <DialogTitle className="text-sm font-semibold leading-tight">
                {t.edgy.drawerTitle}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {t.edgy.drawerSubtitle}
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            {state === "CHECKING" && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t.edgy.checking}
              </div>
            )}

            {state === "MISSING_PREREQS" && !showPlaybookForm && (
              <div className="flex flex-col gap-4">
                <div>
                  <h2 className="text-base font-semibold">{t.edgy.missingPrereqsTitle}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">{t.edgy.missingPrereqsIntro}</p>
                </div>
                <div className="flex flex-col gap-2">
                  {!readiness?.hasPlaybook && (
                    <Button onClick={() => setShowPlaybookForm(true)} className="justify-start">
                      <BookOpen className="mr-2 h-4 w-4" />
                      {t.edgy.addStrategy}
                    </Button>
                  )}
                  {!readiness?.hasTrades && (
                    <Button
                      variant="outline"
                      onClick={() => setShowAddTrade(true)}
                      className="justify-start"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      {t.edgy.addTrades}
                    </Button>
                  )}
                  {/* Playbook and trades both exist, but none of those trades is
                      tagged with the playbook's strategy — so there is nothing to
                      analyse. Without this the panel showed a title and no reason. */}
                  {readiness?.hasPlaybook &&
                    readiness?.hasTrades &&
                    readiness?.strategies.length === 0 && (
                      <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-600 dark:text-amber-400">
                        {t.edgy.noTradesOnStrategy}
                      </p>
                    )}
                </div>
              </div>
            )}

            {state === "MISSING_PREREQS" && showPlaybookForm && (
              <PlaybookForm
                embedded
                onSaved={handlePrereqDone}
                onCancel={() => setShowPlaybookForm(false)}
              />
            )}

            {state === "SELECT_STRATEGY" && (
              <div className="flex flex-col gap-3">
                <div>
                  <h2 className="text-base font-semibold">{t.edgy.selectStrategyTitle}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t.edgy.selectStrategySubtitle}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  {readiness?.strategies.map((s) => (
                    <button
                      key={s.playbookId}
                      type="button"
                      onClick={() => launch(s.playbookId, s.name)}
                      className="flex items-center justify-between rounded-lg border border-border p-3 text-left transition-colors hover:border-primary hover:bg-accent/40"
                    >
                      <div>
                        <p className="text-sm font-medium">{s.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {t.edgy.tradesCount.replace("{count}", String(s.tradesCount))}
                        </p>
                      </div>
                      <span className="font-mono text-xs text-muted-foreground">
                        {t.edgy.versionLabel.replace("{version}", String(s.version))}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {state === "SCANNING" && (
              <EdgyScanSequence
                currentStep={analysis.currentStep}
                context={analysis.scanContext}
                strategyName={selectedStrategy?.name ?? ""}
              />
            )}

            {state === "REVEALING" && parsedReport && (
              <div className="flex flex-col gap-4">
                <EdgeReportView report={parsedReport} />

                <div className="flex flex-col gap-2 border-t border-border pt-3">
                  <p className="text-sm font-medium">{t.edgy.questions.title}</p>

                  {qaThread.map((entry, i) => (
                    <div key={i} className="rounded-md border border-border p-2">
                      <p className="text-xs font-medium">{entry.question}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{entry.answer}</p>
                    </div>
                  ))}

                  {qaThread.length >= 4 ? (
                    <p className="text-xs text-muted-foreground">{t.edgy.questions.limitReached}</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {parsedReport.suggestedQuestions
                        .filter((q) => !answeredQuestions.has(q))
                        .map((q) => (
                          <button
                            key={q}
                            type="button"
                            disabled={!!askingQuestion}
                            onClick={() => handleAskQuestion(q)}
                            className="rounded-full border border-border px-3 py-1.5 text-xs transition-colors hover:border-primary hover:bg-accent/40 disabled:opacity-50"
                          >
                            {askingQuestion === q ? t.edgy.questions.asking : q}
                          </button>
                        ))}
                    </div>
                  )}

                  {questionError && (
                    <p className="text-xs text-destructive">{t.edgy.questions.error}</p>
                  )}
                </div>
              </div>
            )}

            {state === "BETA_LOCKED" && (
              <div className="flex flex-col items-center gap-4 py-6 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500/15 to-teal-500/15 ring-1 ring-inset ring-primary/30">
                  <Lock className="h-6 w-6 text-primary" />
                </span>
                <div className="flex flex-col items-center gap-2">
                  <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
                    {t.edgy.betaLock.badge}
                  </span>
                  <h2 className="text-base font-semibold">{t.edgy.betaLock.title}</h2>
                  <p className="max-w-md text-sm text-muted-foreground">
                    {t.edgy.betaLock.description}
                  </p>
                </div>
                {onUpgrade ? (
                  <Button onClick={onUpgrade}>{t.edgy.betaLock.upgrade}</Button>
                ) : (
                  <Button asChild>
                    <a href="/pricing">{t.edgy.betaLock.upgrade}</a>
                  </Button>
                )}
              </div>
            )}

            {state === "NO_CREDITS" && (
              <div className="flex flex-col gap-3">
                <h2 className="text-base font-semibold">{t.edgy.noCredits.title}</h2>
                <p className="text-sm text-muted-foreground">{t.edgy.noCredits.description}</p>
                {credits?.refillAt && (
                  <p className="text-sm">
                    {t.edgy.noCredits.refillOn.replace(
                      "{date}",
                      new Date(credits.refillAt).toLocaleDateString(
                        language === "fr" ? "fr-FR" : "en-US"
                      )
                    )}
                  </p>
                )}
                {onUpgrade ? (
                  <Button onClick={onUpgrade} className="self-start">{t.edgy.noCredits.upgrade}</Button>
                ) : (
                  <Button asChild className="self-start">
                    <a href="/pricing">{t.edgy.noCredits.upgrade}</a>
                  </Button>
                )}
              </div>
            )}

            {state === "ERROR" && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2 text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  <h2 className="text-base font-semibold">{t.edgy.errors.title}</h2>
                </div>
                <p className="text-sm text-muted-foreground">
                  {readiness?.killSwitch
                    ? t.edgy.errors.killSwitch
                    : analysis.error?.code === "COOLDOWN" && analysis.error.meta?.nextAvailableAt
                      ? t.edgy.errors.cooldown.replace(
                          "{time}",
                          new Date(String(analysis.error.meta.nextAvailableAt)).toLocaleTimeString(
                            language === "fr" ? "fr-FR" : "en-US",
                            { hour: "2-digit", minute: "2-digit" }
                          )
                        )
                      : analysis.error?.code === "NOT_CONFIGURED"
                        ? t.edgy.errors.notConfigured
                        : t.edgy.errors.generic}
                </p>
                {!readiness?.killSwitch && analysis.error?.code !== "NOT_CONFIGURED" && (
                  <Button
                    onClick={() => {
                      reset();
                      setState("CHECKING");
                    }}
                    className="self-start"
                  >
                    {t.edgy.errors.retry}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Credit counter is meaningless on a locked plan — it would read
              "0 remaining" and imply a quota problem rather than a plan one. */}
          {!isLocked && (
            <footer className="border-t border-border px-4 py-2">
              <p className="text-xs text-muted-foreground">
                {edgyCreditsLabel(credits, t.edgy.credits)}
              </p>
            </footer>
          )}
        </DialogContent>
      </Dialog>

      {/* Rendered outside the dialog: nesting one Radix Dialog inside another traps it
          in the parent's focus scope, so it renders as a sibling instead. */}
      <AddTradeModal
        open={showAddTrade}
        onOpenChange={(next) => {
          setShowAddTrade(next);
          if (!next) void handlePrereqDone();
        }}
        onImportDone={() => {
          setShowAddTrade(false);
          void handlePrereqDone();
        }}
      />
    </>
  );
}
